// ============================================================
// cf-checkboxtree.js � Reusable hierarchical checkbox tree
// ============================================================
// Purpose: Render a tree of items with checkboxes for form-input
// use cases (e.g. role ? permissions, user ? menu access,
// category selection, etc.). NOT a data-table � no pagination,
// sorting, or toolbar chrome.
//
// Use this instead of cf-treetable.js when the requirement is:
//   - Form input (checkbox-based selection)
//   - Hierarchical cascade (parent checks ? cascade children)
//   - Bubble up (child toggle ? recompute parent)
//   - Readonly mode (for view dialogs)
//   - Simple search/filter + expand/collapse
//
// Data shape (recursive):
//   [{ id: '...', name: '...', code?: '...', children: [...] }]
//
// Usage:
//   CF.checkboxTree.render('#containerId', {
//       data:         nodes,               // required
//       readonly:     false,                // optional, default false
//       selectedIds:  { id1: true },        // optional pre-selected map
//       idField:      'id',                 // optional, default 'id'
//       nameField:    'name',               // optional, default 'name'
//       codeField:    'code',               // optional, default 'code'
//       childrenField:'children',           // optional, default 'children'
//       emptyText:    'No items found',     // optional
//       assignedBadgeText: 'Assigned',      // optional, readonly mode
//       onChange:     function(ids){ }      // optional
//   });
//
//   var ids   = CF.checkboxTree.getSelected('#containerId');
//   CF.checkboxTree.toggleAll('#containerId', true);   // select all
//   CF.checkboxTree.toggleAll('#containerId', false);  // deselect all
//   CF.checkboxTree.filter('#containerId', 'query');
//   CF.checkboxTree.showLoading('#containerId');
//   CF.checkboxTree.showError('#containerId', 'message');
//
// CSS class convention (shared):
//   .cf-cbt-tree        � outer container (scroll area)
//   .cf-cbt-node        � one node (row + optional children wrapper)
//   .cf-cbt-row         � indented row (toggle + checkbox + label)
//   .cf-cbt-toggle      � expand/collapse button
//   .cf-cbt-leaf-dot    � leaf indicator (no children)
//   .cf-cbt-check-label � label wrapping checkbox + text
//   .cf-cbt-check       � <input type="checkbox">
//   .cf-cbt-name        � display name
//   .cf-cbt-code-tag    � small code tag next to name
//   .cf-cbt-children    � wrapper for child nodes
//   .cf-cbt-empty       � empty state
//   .cf-cbt-error       � error state
//   .cf-cbt-assigned-badge � readonly "assigned" badge
// ============================================================

window.CF = window.CF || {};

(function () {
    'use strict';

    // Track registered trees per container (keyed by element id)
    var _registry = {};

    // ?? Helpers ??????????????????????????????????????????????
    function _getContainer(selectorOrEl) {
        if (!selectorOrEl) return null;
        if (typeof selectorOrEl === 'string') {
            // Accept selector or raw id
            if (selectorOrEl.charAt(0) === '#' || selectorOrEl.indexOf('.') >= 0 || selectorOrEl.indexOf(' ') >= 0) {
                return document.querySelector(selectorOrEl);
            }
            return document.getElementById(selectorOrEl);
        }
        return selectorOrEl;
    }

    function _escHtml(s) {
        if (s === null || s === undefined) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // ?? Build recursive HTML ?????????????????????????????????
    function _buildHtml(nodes, depth, ctx) {
        var html = '';
        var idF       = ctx.idField;
        var nameF     = ctx.nameField;
        var codeF     = ctx.codeField;
        var childF    = ctx.childrenField;
        var readonly  = ctx.readonly;
        var selected  = ctx.selectedIds || {};

        for (var i = 0; i < nodes.length; i++) {
            var node = nodes[i];
            var id   = node[idF];
            var name = node[nameF] || '';
            var code = node[codeF] || '';
            var kids = node[childF] || [];
            var hasChildren = kids && kids.length > 0;
            var isChecked   = !!selected[id];
            var indent      = depth * 24;

            html += '<div class="cf-cbt-node" data-cbt-id="' + _escHtml(id) + '"' +
                ' data-cbt-name="' + _escHtml(String(name).toLowerCase()) + '">';
            html += '<div class="cf-cbt-row" style="padding-left:' + indent + 'px">';

            if (hasChildren) {
                html += '<button type="button" class="cf-cbt-toggle" onclick="CF.checkboxTree._toggleNode(this)">' +
                    '<i class="fa-solid fa-chevron-down"></i></button>';
            } else {
                html += '<span class="cf-cbt-leaf-dot"></span>';
            }

            if (!readonly) {
                html += '<label class="cf-cbt-check-label">' +
                    '<input type="checkbox" class="cf-cbt-check" value="' + _escHtml(id) + '"' +
                    (isChecked ? ' checked' : '') +
                    ' onchange="CF.checkboxTree._onCheck(this)" />' +
                    '<span class="cf-cbt-name">' + _escHtml(name) + '</span>';
                if (code) {
                    html += '<span class="cell-code cf-cbt-code-tag">' + _escHtml(code) + '</span>';
                }
                html += '</label>';
            } else {
                html += '<span class="cf-cbt-name">' + _escHtml(name) + '</span>';
                if (code) {
                    html += '<span class="cell-code cf-cbt-code-tag">' + _escHtml(code) + '</span>';
                }
                if (isChecked) {
                    html += '<span class="badge badge-active badge-sm cf-cbt-assigned-badge">' +
                        '<span class="badge-dot"></span> ' + _escHtml(ctx.assignedBadgeText) + '</span>';
                }
            }

            html += '</div>';

            if (hasChildren) {
                html += '<div class="cf-cbt-children">';
                html += _buildHtml(kids, depth + 1, ctx);
                html += '</div>';
            }

            html += '</div>';
        }
        return html;
    }

    // ?? Public: render ???????????????????????????????????????
    function render(selectorOrEl, opts) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;

        opts = opts || {};
        var ctx = {
            idField:       opts.idField       || 'id',
            nameField:     opts.nameField     || 'name',
            codeField:     opts.codeField     || 'code',
            childrenField: opts.childrenField || 'children',
            readonly:      !!opts.readonly,
            selectedIds:   opts.selectedIds   || {},
            emptyText:     opts.emptyText     || 'No items found',
            assignedBadgeText: opts.assignedBadgeText || 'Assigned',
            onChange:      opts.onChange      || null
        };

        container.classList.add('cf-cbt-tree');

        if (!container.id) {
            container.id = 'cbt-' + Math.random().toString(36).slice(2, 9);
        }

        _registry[container.id] = ctx;

        var data = opts.data || [];
        if (!data.length) {
            container.innerHTML = '<div class="cf-cbt-empty">' +
                '<i class="fa-solid fa-inbox"></i>' + _escHtml(ctx.emptyText) + '</div>';
            return;
        }

        container.innerHTML = _buildHtml(data, 0, ctx);
    }

    // ?? Public: loading / error states ???????????????????????
    function showLoading(selectorOrEl, message) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;
        container.classList.add('cf-cbt-tree');
        container.innerHTML = '<div class="cf-loading-placeholder">' +
            '<i class="fa-solid fa-spinner fa-spin"></i> ' +
            _escHtml(message || 'Loading�') + '</div>';
    }

    function showError(selectorOrEl, message) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;
        container.classList.add('cf-cbt-tree');
        container.innerHTML = '<div class="cf-cbt-error">' +
            _escHtml(message || 'Failed to load.') + '</div>';
    }

    // ?? Public: getSelected ??????????????????????????????????
    function getSelected(selectorOrEl) {
        var container = _getContainer(selectorOrEl);
        if (!container) return [];
        var ids = [];
        var nodes = container.querySelectorAll('.cf-cbt-check:checked');
        for (var i = 0; i < nodes.length; i++) {
            ids.push(nodes[i].value);
        }
        return ids;
    }

    // ?? Public: toggleAll ????????????????????????????????????
    function toggleAll(selectorOrEl, check) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;
        var nodes = container.querySelectorAll('.cf-cbt-check');
        for (var i = 0; i < nodes.length; i++) {
            nodes[i].checked = !!check;
        }
        _fireOnChange(container);
    }

    // ?? Public: filter ???????????????????????????????????????
    function filter(selectorOrEl, query) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;

        var q = (query || '').toLowerCase().trim();
        var nodes = container.querySelectorAll('.cf-cbt-node');

        if (!q) {
            for (var i = 0; i < nodes.length; i++) nodes[i].style.display = '';
            return;
        }

        // First pass � hide all
        for (var j = 0; j < nodes.length; j++) nodes[j].style.display = 'none';

        // Second pass � show matches + ancestors
        for (var k = 0; k < nodes.length; k++) {
            var n = nodes[k];
            var name = n.dataset.cbtName || '';
            if (name.indexOf(q) >= 0) {
                n.style.display = '';
                var parent = n.parentElement ? n.parentElement.closest('.cf-cbt-node') : null;
                while (parent) {
                    parent.style.display = '';
                    parent = parent.parentElement ? parent.parentElement.closest('.cf-cbt-node') : null;
                }
            }
        }
    }

    // ?? Public: expand/collapse all ??????????????????????????
    function expandAll(selectorOrEl) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;
        var kids = container.querySelectorAll('.cf-cbt-children');
        for (var i = 0; i < kids.length; i++) kids[i].style.display = '';
        var icons = container.querySelectorAll('.cf-cbt-toggle i');
        for (var j = 0; j < icons.length; j++) icons[j].className = 'fa-solid fa-chevron-down';
    }

    function collapseAll(selectorOrEl) {
        var container = _getContainer(selectorOrEl);
        if (!container) return;
        var kids = container.querySelectorAll('.cf-cbt-children');
        for (var i = 0; i < kids.length; i++) kids[i].style.display = 'none';
        var icons = container.querySelectorAll('.cf-cbt-toggle i');
        for (var j = 0; j < icons.length; j++) icons[j].className = 'fa-solid fa-chevron-right';
    }

    // ?? Internal callbacks (called from inline onclick) ??????
    function _toggleNode(btn) {
        var node = btn.closest('.cf-cbt-node');
        if (!node) return;
        var children = node.querySelector(':scope > .cf-cbt-children');
        var icon = btn.querySelector('i');
        if (!children) return;

        if (children.style.display === 'none') {
            children.style.display = '';
            if (icon) icon.className = 'fa-solid fa-chevron-down';
        } else {
            children.style.display = 'none';
            if (icon) icon.className = 'fa-solid fa-chevron-right';
        }
    }

    function _onCheck(checkbox) {
        // Cascade down: all descendants match parent state
        var node = checkbox.closest('.cf-cbt-node');
        if (node) {
            var children = node.querySelectorAll('.cf-cbt-children .cf-cbt-check');
            for (var i = 0; i < children.length; i++) {
                children[i].checked = checkbox.checked;
            }
        }
        // Bubble up: any child checked ? parent checked; none checked ? parent unchecked
        _updateParentChecks(checkbox);
        _fireOnChange(checkbox);
    }

    function _updateParentChecks(checkbox) {
        var parentChildren = checkbox.closest('.cf-cbt-children');
        if (!parentChildren) return;

        var parentNode = parentChildren.closest('.cf-cbt-node');
        if (!parentNode) return;

        var parentCheck = parentNode.querySelector(':scope > .cf-cbt-row .cf-cbt-check');
        if (!parentCheck) return;

        var siblings = parentChildren.querySelectorAll(':scope > .cf-cbt-node > .cf-cbt-row .cf-cbt-check');
        var anyChecked = false;
        for (var i = 0; i < siblings.length; i++) {
            if (siblings[i].checked) { anyChecked = true; break; }
        }
        parentCheck.checked = anyChecked;
        _updateParentChecks(parentCheck);
    }

    function _fireOnChange(elOrContainer) {
        // Walk up to find container id and invoke onChange if configured
        var container = elOrContainer;
        if (!container) return;
        if (!(container.classList && container.classList.contains('cf-cbt-tree'))) {
            container = elOrContainer.closest ? elOrContainer.closest('.cf-cbt-tree') : null;
        }
        if (!container || !container.id) return;
        var ctx = _registry[container.id];
        if (!ctx || typeof ctx.onChange !== 'function') return;
        ctx.onChange(getSelected(container));
    }

    // ?? Public API ???????????????????????????????????????????
    CF.checkboxTree = {
        render:      render,
        showLoading: showLoading,
        showError:   showError,
        getSelected: getSelected,
        toggleAll:   toggleAll,
        filter:      filter,
        expandAll:   expandAll,
        collapseAll: collapseAll,
        // Internal � do not call directly from page scripts
        _toggleNode: _toggleNode,
        _onCheck:    _onCheck
    };
})();
